import React from 'react';
import { Link } from 'react-router-dom';
import { FAQS, FAQ_CATEGORY_DEFS } from './faqs';
import { AJANLAT_PAGE_URL } from './campaigns';
import gyikIllu from '../img/gyik.png';
import '../App.css';

function FaqGlyph({ name }) {
  const svg = {
    width: 22,
    height: 22,
    viewBox: '0 0 24 24',
    fill: 'none',
    xmlns: 'http://www.w3.org/2000/svg',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  };
  switch (name) {
    case 'clock':
      return (
        <svg {...svg}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
      );
    case 'globe':
      return (
        <svg {...svg}>
          <circle cx="12" cy="12" r="9" />
          <path d="M2.5 12h19M12 2.5c2.5 4 2.5 15 0 19M12 2.5c-2.5 4-2.5 15 0 19" />
        </svg>
      );
    case 'box':
      return (
        <svg {...svg}>
          <path d="M12 2L3 7v10l9 5 9-5V7l-9-5zM3 7l9 5 9-5M12 12v10" />
        </svg>
      );
    case 'shield':
      return (
        <svg {...svg}>
          <path d="M12 3l8 4v5c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V7l8-4z" />
        </svg>
      );
    case 'phone':
      return (
        <svg {...svg}>
          <path d="M5 4h4l2 5-2 1a12 12 0 006 6l1-2 5 2v4a1 1 0 01-1 1C9 17 4 10 3 5a1 1 0 011-1z" />
        </svg>
      );
    case 'truck':
      return (
        <svg {...svg}>
          <path d="M2 7h9v9H2zM11 11h5l3 3v2h-8v-5z" />
          <circle cx="5.5" cy="18" r="1.75" />
          <circle cx="18" cy="18" r="1.75" />
        </svg>
      );
    case 'thermometer':
      return (
        <svg {...svg}>
          <path d="M14 4v10.54a4 4 0 11-4 0V4a2 2 0 114 0z" />
        </svg>
      );
    case 'tag':
      return (
        <svg {...svg}>
          <path d="M12.5 2H19a1 1 0 011 1v6.5a1 1 0 01-.3.7L12 18l-7.6-7.6a1 1 0 010-1.4l7.1-7.1a1 1 0 01.7-.3zM15 6h.01" />
        </svg>
      );
    case 'layers':
      return (
        <svg {...svg}>
          <path d="M12 2l9 5-9 5-9-5 9-5zM3 12l9 5 9-5M3 17l9 5 9-5" />
        </svg>
      );
    case 'cube':
      return (
        <svg {...svg}>
          <path d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3zM12 12l8-4.5M12 12v9M12 12L4 7.5" />
        </svg>
      );
    default:
      return (
        <svg {...svg}>
          <circle cx="12" cy="12" r="3" />
        </svg>
      );
  }
}

export default function FaqPage() {
  const [query, setQuery] = React.useState('');
  const [categoryId, setCategoryId] = React.useState(null);
  const searchRef = React.useRef(null);
  const [kbdLabel, setKbdLabel] = React.useState('Ctrl+K');

  React.useEffect(() => {
    const isMac =
      typeof navigator !== 'undefined' &&
      (/Mac|iPhone|iPad|iPod/i.test(navigator.platform) ||
        (navigator.userAgentData && navigator.userAgentData.platform === 'macOS'));
    setKbdLabel(isMac ? '⌘K' : 'Ctrl+K');
  }, []);

  React.useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const countsByCategory = React.useMemo(() => {
    const m = {};
    for (const f of FAQS) {
      const id = f.category;
      if (!id) continue;
      m[id] = (m[id] || 0) + 1;
    }
    return m;
  }, []);

  const categoryCards = React.useMemo(
    () => FAQ_CATEGORY_DEFS.filter((c) => (countsByCategory[c.id] || 0) > 0),
    [countsByCategory],
  );

  const filtered = React.useMemo(() => {
    let list = FAQS;
    if (categoryId) {
      list = list.filter((f) => f.category === categoryId);
    }
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((f) => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q));
    }
    return list;
  }, [query, categoryId]);

  const total = FAQS.length;

  const metaLine = (() => {
    if (query.trim()) {
      return `${filtered.length} találat / ${total} kérdésből`;
    }
    if (categoryId) {
      const n = countsByCategory[categoryId] || 0;
      return `${n} kérdés ebben a kategóriában`;
    }
    return `Összesen ${total} kérdés`;
  })();

  return (
    <main className="faq-page">
      <section className="faq-hero" aria-labelledby="faq-hero-title">
        <div className="faq-hero-inner">
          <nav className="faq-breadcrumb" aria-label="Útvonal">
            <Link to="/" className="faq-breadcrumb-home" aria-label="Főoldal">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M4 10.5L12 4l8 6.5V20a1 1 0 01-1 1h-5v-7H10v7H5a1 1 0 01-1-1v-9.5z"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinejoin="round"
                />
              </svg>
            </Link>
            <span className="faq-breadcrumb-sep" aria-hidden="true">
              /
            </span>
            <span className="faq-breadcrumb-current">GYIK</span>
          </nav>

          <div className="faq-hero-grid">
            <div className="faq-hero-text">
              <h1 id="faq-hero-title" className="faq-hero-title">
                GYIK – minden, amit érdemes tudni
              </h1>
              <p className="faq-hero-lead">
                Kereshetsz kulcsszóra, szűrhetsz témakör szerint, vagy böngészhetsz a kérdések között. Ha nem
                találod a választ, írj nekünk – szívesen segítünk.
              </p>
            </div>

            <div className="faq-hero-visual">
              <div className="faq-hero-visual-frame">
                <img
                  src={gyikIllu}
                  alt="Illusztráció: szállítás és gyakori kérdések"
                  width={480}
                  height={480}
                  loading="eager"
                  decoding="async"
                />
              </div>
            </div>

            <div className="faq-hero-search">
              <div className="faq-search-block faq-search-block--wide">
                <label className="visually-hidden" htmlFor="faq-query">
                  Keresés a kérdések között
                </label>
                <div className="faq-search-input-wrap">
                  <span className="faq-search-icon" aria-hidden="true">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path
                        d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M15.8 15.8 21 21"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                  <input
                    ref={searchRef}
                    id="faq-query"
                    type="search"
                    className="faq-search-input"
                    placeholder="Keress a kérdések között…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    autoComplete="off"
                  />
                  <kbd className="faq-search-kbd" aria-hidden="true">
                    {kbdLabel}
                  </kbd>
                </div>
                <p className="faq-search-meta" role="status" aria-live="polite">
                  <span className="faq-search-meta-icon" aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinejoin="round"
                      />
                      <path d="M15.8 15.8 21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </span>
                  {metaLine}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="faq-list-section" aria-label="Kérdések és válaszok">
        <div className="faq-list-inner faq-list-inner--wide">
          {categoryCards.length > 0 ? (
            <div className="faq-category-row" role="group" aria-label="Témakör szerinti szűrés">
              <button
                type="button"
                className={`faq-cat-card${categoryId === null ? ' faq-cat-card--active' : ''}`}
                onClick={() => setCategoryId(null)}
                aria-pressed={categoryId === null}
              >
                <span className="faq-cat-card-icon-wrap" aria-hidden="true">
                  <span className="faq-cat-card-icon">
                    <FaqGlyph name="layers" />
                  </span>
                </span>
                <span className="faq-cat-card-label">Összes</span>
                <span className="faq-cat-card-count">{total} kérdés</span>
              </button>
              {categoryCards.map((c) => {
                const n = countsByCategory[c.id] || 0;
                return (
                  <button
                    key={c.id}
                    type="button"
                    className={`faq-cat-card${categoryId === c.id ? ' faq-cat-card--active' : ''}`}
                    onClick={() => setCategoryId(c.id)}
                    aria-pressed={categoryId === c.id}
                  >
                    <span className="faq-cat-card-icon-wrap" aria-hidden="true">
                      <span className="faq-cat-card-icon">
                        <FaqGlyph name={c.cardIcon} />
                      </span>
                    </span>
                    <span className="faq-cat-card-label">{c.label}</span>
                    <span className="faq-cat-card-count">{n} kérdés</span>
                  </button>
                );
              })}
            </div>
          ) : null}

          <div className="faq-accordion">
            {filtered.length === 0 ? (
              <div className="faq-empty-card">
                <p className="faq-empty-title">Nincs találat</p>
                <p className="faq-empty">
                  Próbálj más kulcsszót vagy témakört, vagy{' '}
                  <Link className="faq-empty-link" to="/">
                    lépj vissza a főoldalra
                  </Link>
                  , és vedd fel velünk a kapcsolatot.
                </p>
              </div>
            ) : (
              filtered.map((f) => (
                <details key={f.q} className="faq-acc-item" name="gyik-faq">
                  <summary className="faq-acc-summary">
                    <span className="faq-acc-icon-wrap" aria-hidden="true">
                      <FaqGlyph name={f.icon || 'clock'} />
                    </span>
                    <span className="faq-acc-q">{f.q}</span>
                    <span className="faq-acc-chevron" aria-hidden="true" />
                  </summary>
                  <div className="faq-acc-panel">
                    <p className="faq-acc-a">{f.a}</p>
                  </div>
                </details>
              ))
            )}
          </div>

          <div className="faq-bottom-cta faq-bottom-cta--banner">
            <div className="faq-bottom-cta-icon" aria-hidden="true">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path
                  d="M4 10v2a8 8 0 008 8h1M4 10a8 8 0 018-8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2M4 10l2-2m0 0h3m-3 0v4"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <p className="faq-bottom-cta-text">
              Nem találod, amit keresel? Vedd fel velünk a kapcsolatot, kollégáink szívesen segítenek!
            </p>
            <div className="faq-bottom-cta-actions">
              <Link className="faq-cta-outline" to="/#kapcsolat">
                Kapcsolatfelvétel
              </Link>
              <Link className="faq-cta-primary" to={AJANLAT_PAGE_URL}>
                Ajánlatkérés
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
